import { useCallback } from 'react';

import { scrollToToday } from 'pages/ParentHelper/utils';
import { usePostApiParentHelperAskQuestionMutation } from 'shared/api/gsp';
import { AnalyticsEvent } from 'shared/constants/analytics';
import { useAnalytics } from 'shared/hooks/analytics';

import { useShouldScrollToTodaySection, useSetChatHistory } from './hooks';

import type {
  ChatId,
  Message,
  Question,
  SetChatId,
  SetChatTopics,
  Topic,
} from 'pages/ParentHelper/types';
import type { Dispatch, SetStateAction } from 'react';

interface UseSendQuestionArgs {
  chatTopics: Topic[];
  setChatTopics: SetChatTopics;
  setData: Dispatch<SetStateAction<Message[]>>;
  chatId: ChatId;
  setChatId: SetChatId;
  setShouldScrollToBottom: Dispatch<SetStateAction<boolean>>;
}

export const useSendQuestion = ({
  chatTopics,
  setChatTopics,
  setData,
  chatId,
  setChatId,
  setShouldScrollToBottom,
}: UseSendQuestionArgs) => {
  const [
    postQuestion,
    { isLoading: isLoadingAnswer, isError: isErrorSendQuestion },
  ] = usePostApiParentHelperAskQuestionMutation();
  const analytics = useAnalytics();
  const setChatHistory = useSetChatHistory(chatTopics, chatId, setChatTopics);
  const shouldScrollToTodaySection = useShouldScrollToTodaySection(
    chatTopics,
    chatId,
  );

  const sendQuestion = useCallback(
    (questionData: Question) => {
      setData(prevData => [
        ...prevData,
        {
          id: questionData.question.slice(0, 5),
          question: questionData.question,
        },
      ]);

      setShouldScrollToBottom(true);

      postQuestion({
        inspiredGlobalStudyPlatformApiModelsParentHelperPostQuestionRequest:
          questionData,
      })
        .unwrap()
        .then(data => {
          setData(prevData => [
            ...prevData,
            {
              id: data.messageId,
              answer: data.answer,
            },
          ]);

          setChatHistory(data.chatId, questionData.question);

          if (shouldScrollToTodaySection) {
            scrollToToday();
          }

          if (data?.chatId !== chatId) {
            setChatId(data.chatId);
          }

          analytics.track(
            AnalyticsEvent.AIParentHelperChatbotSendMessageButtonClicked,
          );
        })
        .catch(error => error);
    },
    [
      setData,
      postQuestion,
      chatId,
      analytics,
      setChatId,
      setShouldScrollToBottom,
      setChatHistory,
      shouldScrollToTodaySection,
    ],
  );

  return {
    sendQuestion,
    isLoadingAnswer,
    isErrorSendQuestion,
  };
};
